import React, { memo } from 'react';
import { Box, IconButton, Typography, alpha } from '@mui/material';
import { FreeBreakfastOutlined, MoreHoriz } from '@mui/icons-material';
import type { TimetableRow } from '../../../services/timetableService';
import { toMinutes } from '../timetableUtils';
import { LightInput } from './LightFields';

interface BellRowProps {
  row: TimetableRow;
  index: number;
  /** The period's number, or null for a break. */
  number: number | null;
  problem?: string;
  readOnly: boolean;
  canWrite: boolean;
  onLabel: (index: number, label: string) => void;
  onTime: (index: number, field: 'start' | 'end', value: string) => void;
  onMenu: (anchor: HTMLElement, index: number) => void;
}

/** One row of the bell schedule. Memoised: editing one row redraws only the rows that change. */
const BellRow = memo<BellRowProps>(({ row, index, number, problem, readOnly, canWrite, onLabel, onTime, onMenu }) => {
  const isBreak = row.kind === 'break';
  const minutes = toMinutes(row.end) - toMinutes(row.start);
  return (
    <Box>
      <Box
        sx={(theme) => ({
          display: 'flex', alignItems: 'center', gap: 1.25, px: 1.25, py: 0.75, borderRadius: '10px',
          bgcolor: isBreak ? alpha(theme.palette.text.secondary, 0.06) : 'transparent',
          border: '1px solid', borderColor: problem ? 'error.main' : isBreak ? 'transparent' : 'divider',
        })}
      >
        <Box sx={{ width: 28, display: 'flex', justifyContent: 'center', color: isBreak ? 'text.secondary' : 'primary.main' }}>
          {isBreak ? <FreeBreakfastOutlined fontSize="small" /> : <Typography variant="caption" fontWeight={700}>{number}</Typography>}
        </Box>
        <LightInput
          placeholder={isBreak ? 'Break' : 'Period'} value={row.label ?? ''} disabled={readOnly}
          onChange={(e) => onLabel(index, e.target.value)}
          sx={{ flex: 1, minWidth: 80, border: 0, bgcolor: 'transparent', fontWeight: isBreak ? 500 : 600, '&.Mui-focused': { boxShadow: 'none' } }}
        />
        <LightInput type="time" value={row.start} disabled={readOnly} onChange={(e) => onTime(index, 'start', e.target.value)} sx={{ width: 128 }} inputProps={{ 'aria-label': 'Starts' }} />
        <Typography variant="caption" color="text.disabled">to</Typography>
        <LightInput type="time" value={row.end} disabled={readOnly} onChange={(e) => onTime(index, 'end', e.target.value)} sx={{ width: 128 }} inputProps={{ 'aria-label': 'Ends' }} />
        <Typography variant="caption" color="text.secondary" sx={{ width: 48, textAlign: 'right' }}>{minutes > 0 ? `${minutes} min` : ''}</Typography>
        {canWrite && (
          <IconButton size="small" aria-label="Row actions" onClick={(e) => onMenu(e.currentTarget, index)}>
            <MoreHoriz fontSize="small" />
          </IconButton>
        )}
      </Box>
      {problem && <Typography variant="caption" color="error" sx={{ pl: 6 }}>{problem}</Typography>}
    </Box>
  );
});

BellRow.displayName = 'BellRow';

export default BellRow;
